// SoporteJuridico.tsx

import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../security/AuthLogin";
import {
  fetchSoporteMensajes,
  sendSoporteMensaje,
  fetchArchivosSoporte,
} from "../api/soportesApi";
import { getAuthToken } from "../security/AuthLogin";
import { io, Socket } from "socket.io-client";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";
import "../css/favorito.css";
import ChatComponent from "../components/ChatComponent";

/**
 * INTERFACES
 */
interface SoporteJuridicoData {
  id_concurso: string;
  id_usuario: string;
  titulo: string;
  codigo_expediente: string;
  enlace: string | null;
  estado_concurso: string;
  estado_soporte_favorito: string | null;
  id_soporte: number;
  cliente_nombre?: string;
  cliente_email?: string;
  cliente_profilepic?: string;
  dueno_nombre?: string | null;
  dueno_email?: string | null;
}

// Mantenemos la misma interfaz para mensajes
interface MensajeSoporte {
  id_soporte_detalle: number;
  id_soporte: number;
  id_usuario: string;
  f_respuesta: string;
  hora_respuesta: string;
  texto: string;
  es_cliente: boolean;
}

// <-- CAMBIO: Usamos dateFolder en lugar de timestamp para la fecha (formato "YYYY-MM-DD")
interface ArchivoSoporte {
  path: string;
  uploaderRole: "cliente" | "admin" | "tecnico";
  dateFolder?: string | null;
}

interface TecnicoData {
  id_usuario: string;
  nombre: string;
  email: string;
}

const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;


function mapEstado(estado: string | null): string {
  if (!estado) return "En estudio";
  switch (estado.toLowerCase()) {
    case "en estudio":
      return "En estudio";
    case "valido":
      return "Viable";
    case "rechazado":
      return "No viable";
    case "oculto":
      return "Oculto";
    case "norecomendado":
      return "No recomendado";
    default:
      return estado.charAt(0).toUpperCase() + estado.slice(1);
  }
}

/**
 * COMPONENT
 */
const SoporteJuridico: React.FC = () => {
  const { id_concurso, id_usuario } = useParams<{
    id_concurso: string;
    id_usuario: string;
  }>();
  const { userRole } = useAuth();

  const [loading, setLoading] = useState(false);
  const [soporteJuridico, setSoporteJuridico] =
    useState<SoporteJuridicoData | null>(null);
  const [, setMensajes] = useState<MensajeSoporte[]>([]);
  const [, setArchivos] = useState<ArchivoSoporte[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [, setAlguienTyping] = useState({
    isTyping: false,
    quien: "",
  });
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const [tecnicosActivos, setTecnicosActivos] = useState<TecnicoData[]>([]);
  const [selectedTecnico, setSelectedTecnico] = useState("");
  const [showReassignModal, setShowReassignModal] = useState(false);

  // ★ NUEVO: confirmación modal estados
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmType, setConfirmType] = useState<
    "baja" | "tecnico" | "administrativo" | null
  >(null);
  const [confirmMessage, setConfirmMessage] = useState("");

  // ★ NUEVO: configuración tipos
  const tipoConfig = {
    baja: {
      label: "Baja estadística",
      endpoint: "/api/baja-estadistica",
      redirect: (c: string, u: string) => `/baja-estadistica/${c}/${u}`,
    },
    tecnico: {
      label: "Soporte técnico",
      endpoint: "/api/soporte-tecnico",
      redirect: (c: string, u: string) => `/soporte-tecnico/${c}/${u}`,
    },
    administrativo: {
      label: "Soporte administrativo",
      endpoint: "/api/soporte-administrativo",
      redirect: (c: string, u: string) => `/soporte-administrativo/${c}/${u}`,
    },
  };

  // ★ NUEVO: comprueba existencia
  const checkSupportExists = async (
    type: keyof typeof tipoConfig
  ): Promise<boolean> => {
    if (!soporteJuridico) return false;
    const token = getAuthToken();
    const res = await fetch(
      `${BASE_URL}/api/soporte-existe?tipo=${type}&id_concurso=${soporteJuridico.id_concurso}&id_usuario=${soporteJuridico.id_usuario}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!res.ok) return false;
    const json = await res.json();
    return json.exists;
  };

  // ★ NUEVO: handler wrapper
  const handleButtonClick = async (type: keyof typeof tipoConfig) => {
    if (!soporteJuridico) return;
    const exists = await checkSupportExists(type);
    if (exists) {
      invokeSupport(type, "");
    } else {
      setConfirmType(type);
      setConfirmMessage("");
      setConfirmModalVisible(true);
    }
  };

  // ★ NUEVO: invoca POST + mensaje + redirección
  const invokeSupport = async (
    type: keyof typeof tipoConfig,
    initialMsg: string
  ) => {
    if (!soporteJuridico) return;
    const cfg = tipoConfig[type];
    try {
      const token = getAuthToken();
      const res = await fetch(cfg.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id_concurso: soporteJuridico.id_concurso,
          id_usuario: soporteJuridico.id_usuario,
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || "Error");
      const data = await res.json();
      if (data.success && data.id_soporte) {
        if (initialMsg.trim()) {
          await sendSoporteMensaje(
            data.id_soporte,
            DOMPurify.sanitize(initialMsg)
          );
        }
        window.location.href = cfg.redirect(
          soporteJuridico.id_concurso,
          soporteJuridico.id_usuario
        );
      } else {
        alert("No se obtuvo ID de soporte");
      }
    } catch (e: any) {
      console.error(e);
      alert(e.message || "Error procesando solicitud");
    }
  };

  const handleConfirmYes = () => {
    if (confirmType) {
      invokeSupport(confirmType, confirmMessage);
      setConfirmModalVisible(false);
      setConfirmType(null);
      setConfirmMessage("");
    }
  };
  const handleConfirmNo = () => {
    setConfirmModalVisible(false);
    setConfirmType(null);
    setConfirmMessage("");
  };

  /**
   * SOCKET
   */
  useEffect(() => {
    const s = io(BASE_URL, {
      withCredentials: true,
      auth: (cb) => cb({ token: localStorage.getItem("authToken") }),
    });
    setSocket(s);
    return () => {
      s.disconnect();
    };
  }, []);

  /**
   * CARGA DE SOPORTE JURÍDICO
   */
  useEffect(() => {
    if (id_concurso && id_usuario) {
      loadSoporteJuridico(id_concurso, id_usuario);
    }
  }, [id_concurso, id_usuario]);

  const loadSoporteJuridico = async (
    concursoId: string,
    userIdParam: string
  ) => {
    setLoading(true);
    try {
      const token = getAuthToken();
      if (!token) throw new Error("No autenticado");

      // Ruta de detalle del soporte jurídico (id_tipo_soporte=3)
      const resp = await fetch(
        `${BASE_URL}/api/soportes-juridicos/detalle/${concursoId}/${userIdParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || "Error al cargar soporte jurídico");
      }
      const data = await resp.json();
      if (data.success && data.soporte_juridico) {
        setSoporteJuridico(data.soporte_juridico);
        // Cargar chat y archivos
        if (data.soporte_juridico.id_soporte) {
          await loadChat(data.soporte_juridico.id_soporte);
          await loadArchivos(data.soporte_juridico.id_soporte);
        }
      }
    } catch (error: any) {
      console.error("Error loadSoporteJuridico:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadChat = async (idSoporte: number) => {
    try {
      const resp = await fetchSoporteMensajes(idSoporte);
      if (resp.success) {
        setMensajes(resp.messages);
        scrollToBottom();
      }
    } catch (error: any) {
      console.error("Error loading chat:", error);
    }
  };

  const loadArchivos = async (idSoporte: number) => {
    try {
      const resp = await fetchArchivosSoporte(idSoporte);
      if (resp.success) setArchivos(resp.archivos);
    } catch (error: any) {
      console.error("Error loading files:", error);
    }
  };

  useEffect(() => {
    if (socket && soporteJuridico?.id_soporte) {
      socket.emit("joinSoporte", soporteJuridico.id_soporte);
      socket.on("nuevoMensaje", (msg: MensajeSoporte) => {
        setMensajes((prev) => [...prev, msg]);
        scrollToBottom();
      });
      socket.on("alguienEstaEscribiendo", ({ isTyping, quien }) => {
        setAlguienTyping({ isTyping, quien });
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        if (isTyping) {
          typingTimeoutRef.current = setTimeout(() => {
            setAlguienTyping({ isTyping: false, quien: "" });
          }, 5000);
        }
      });
    }
    return () => {
      if (socket) {
        socket.off("nuevoMensaje");
        socket.off("alguienEstaEscribiendo");
      }
    };
  }, [socket, soporteJuridico?.id_soporte]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  /**
   * CARGA DE TÉCNICOS PARA REASIGNAR
   */
  useEffect(() => {
    if (userRole === "admin") {
      fetchTecnicos();
    }
  }, [userRole]);

  const fetchTecnicos = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const resp = await fetch(`${BASE_URL}/api/usuarios/tecnicos-activos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) {
        setTecnicosActivos(json.tecnicos);
      }
    } catch (error) {
      console.error("Error fetching technicians:", error);
    }
  };

  /**
   * LÓGICA DE DUEÑOS: Asignar, liberar, reasignar
   */
  const handleDueñoAction = async (
    action: "becomeOwner" | "dropOwner" | "assignTo",
    newDueno?: string
  ) => {
    if (!soporteJuridico?.id_soporte) return;
    try {
      const resp = await fetch(
        `${BASE_URL}/api/soportes/${soporteJuridico.id_soporte}/dueno`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ action, ...(newDueno && { newDueno }) }),
        }
      );
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Error al actualizar dueño: ${txt}`);
      }
      const data = await resp.json();
      if (data.success) {
        // Recargar soporte jurídico
        await loadSoporteJuridico(
          soporteJuridico.id_concurso,
          soporteJuridico.id_usuario
        );
        alert(data.message);
      } else {
        alert(data.message || "Error al cambiar dueño");
      }
    } catch (error: any) {
      console.error("Error updating owner:", error);
      alert(`Error al cambiar dueño: ${error.message}`);
    }
  };

  const handleReassignModalOpen = () => {
    setShowReassignModal(true);
  };

  const handleReassignConfirm = async () => {
    if (!selectedTecnico) {
      alert("Selecciona un técnico");
      return;
    }
    await handleDueñoAction("assignTo", selectedTecnico);
    setShowReassignModal(false);
  };

  /**
   * RENDER
   */
  if (loading) {
    return (
      <div className="concurso-container">
        <p>Cargando soporte jurídico...</p>
      </div>
    );
  }

  if (!soporteJuridico) {
    return (
      <div className="concurso-container">
        <p>No se encontró el soporte jurídico</p>
      </div>
    );
  }

  const soyDueño = false; // Ajustar según corresponda
  const esAdmin = userRole === "admin";
  const esTec = userRole === "tecnico";
  const esAdminOTec = esAdmin || esTec;
  const hayDueño = !!soporteJuridico.dueno_nombre;
  const estadoVisible = mapEstado(
    soporteJuridico.estado_soporte_favorito || "En estudio"
  );

  return (
    <div className="favorito-page">
      {/* Botones flotantes */}
      <aside className="sticky-sidebar">
        <button className="sidebar-button">
          <Link
            to={`/concurso/${soporteJuridico.id_concurso}`}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            Ver concurso
          </Link>
        </button>
        <button className="sidebar-button">
          <Link
            to={`/favorito/${soporteJuridico.id_concurso}/${soporteJuridico.id_usuario}`}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            Ver favorito
          </Link>
        </button>
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("baja")}
        >
          Baja estadística
        </button>
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("tecnico")}
        >
          Soporte Técnico
        </button>
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("administrativo")}
        >
          Soporte Administrativo
        </button>
      </aside>

      <div className="concurso-container">
        <div
          className="concurso-header"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div>
            <h1>Soporte Jurídico</h1>
            <p>Expediente: {soporteJuridico.codigo_expediente}</p>
            {esAdminOTec &&
              (hayDueño ? (
                <p>
                  Responsable: {soporteJuridico.dueno_nombre} (
                  {soporteJuridico.dueno_email})
                </p>
              ) : (
                <p>Sin responsable asignado</p>
              ))}
          </div>
          {esAdminOTec && (
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              {!hayDueño && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleDueñoAction("becomeOwner")}
                >
                  Asignarme
                </button>
              )}
              {hayDueño && (soyDueño || esAdmin) && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleDueñoAction("dropOwner")}
                >
                  Liberar
                </button>
              )}
              {esAdmin && (
                <button
                  className="btn btn-secondary"
                  onClick={handleReassignModalOpen}
                >
                  Reasignar
                </button>
              )}
            </div>
          )}
        </div>

        {showReassignModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Reasignar soporte</h3>
              <select
                value={selectedTecnico}
                onChange={(e) => setSelectedTecnico(e.target.value)}
              >
                <option value="">Seleccionar técnico...</option>
                {tecnicosActivos.map((t) => (
                  <option key={t.id_usuario} value={t.id_usuario}>
                    {t.nombre} ({t.email})
                  </option>
                ))}
              </select>
              <div style={{ marginTop: "1rem" }}>
                <button
                  onClick={handleReassignConfirm}
                  style={{ marginRight: "1rem" }}
                >
                  Confirmar
                </button>
                <button onClick={() => setShowReassignModal(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="section">
          <h2>Detalles</h2>
          <div className="info-grid">
            <div className="field">
              <label>Título</label>
              <span>{soporteJuridico.titulo}</span>
            </div>
            <div className="enlaces-container">
              <label>Enlace</label>
              {soporteJuridico.enlace ? (
                <a
                  href={soporteJuridico.enlace}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="enlace-oficial"
                >
                  Enlace oficial
                </a>
              ) : (
                <span>No disponible</span>
              )}
            </div>
          </div>
          <div style={{ marginTop: "1rem" }}>
            <strong>Estado Soporte:</strong> <span>{estadoVisible}</span>
          </div>
        </div>

        <div className="section">
          <h2>Comunicación</h2>
          <ChatComponent
            idSoporte={soporteJuridico.id_soporte}
            clienteNombre={soporteJuridico.cliente_nombre}
            clienteEmail={soporteJuridico.cliente_email}
            clienteProfilePic={soporteJuridico.cliente_profilepic}
            sendText={true}
            allowFileUpload={true}
            showTypingIndicator={true}
          />
        </div>
      </div>
      {confirmModalVisible && confirmType && (
        <div className="modal-overlay-soportes">
          <div className="modal-content-soportes">
            <h3>
              ¿Quieres solicitar el{" "}
              <strong>{tipoConfig[confirmType].label}</strong>?
            </h3>
            <textarea
              value={confirmMessage}
              onChange={(e) => setConfirmMessage(e.target.value)}
              placeholder="Describe tu solicitud…"
              rows={4}
              style={{ width: "100%", margin: "1rem 0" }}
            />
            <div className="modal-buttons-soportes">
              <button
                onClick={handleConfirmYes}
                disabled={!confirmMessage.trim()}
              >
                Sí
              </button>
              <button onClick={handleConfirmNo}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoporteJuridico;
